/**
 * Ownership date helpers (SCRUM-172) — timezone-safe date-only
 * round-tripping for the Owner tab.
 *
 * The ownership workflow is **date-only** (owner-since, ended-on,
 * transfer date) but the BE schema requires full ISO date-time strings
 * (`z.string().datetime()`). The naive round-trip —
 * `new Date(localDate).toISOString()` in → `new Date(iso).toLocaleDateString()`
 * out — drifts a day in timezones west of UTC (Codex round-1 finding):
 * a date picked as `2026-06-01` renders as `May 31, 2026` in
 * `America/Hermosillo`, because the stored value is UTC midnight and the
 * default `toLocaleDateString` shifts it into the local (previous) day.
 *
 * Fix: anchor both ends to **UTC**.
 *   - `dateOnlyToIso` builds the instant explicitly at UTC midnight.
 *   - `formatOwnershipDate` renders with `timeZone: 'UTC'`.
 * So a selected calendar day renders back as the same calendar day in
 * every viewer timezone.
 */

/**
 * Convert a `<input type="date">` value (`YYYY-MM-DD`) to a UTC-midnight
 * ISO date-time string, or `undefined` when blank / invalid.
 */
export function dateOnlyToIso(localDate: string): string | undefined {
  if (localDate.length === 0) return undefined;
  // Anchor explicitly at UTC midnight — never let the runtime's local
  // zone enter the conversion.
  const iso = `${localDate}T00:00:00.000Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Render an ISO date-time as a short date, anchored to UTC so a
 * date-only value never drifts across timezones.
 */
export function formatOwnershipDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
